import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Link,
  CircularProgress,
  Divider,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import { formatCurrency } from '../../utils/formatting';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import {
  formatSettlementEntryLabel,
  formatSettlementEntryType,
} from './settlementEntryLabels';

const money = (value) => formatCurrency(Number(value) || 0);

const formatTs = (ts) => {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('tr-TR');
  } catch {
    return '—';
  }
};

/**
 * Düzeltme satırı detay modalı — tıklanınca açılır; admin silebilir.
 */
const SettlementEntryDetailModal = ({ open, onClose, entry, isAdmin, onDelete }) => {
  const translate = useTranslate();
  const [deleting, setDeleting] = useState(false);
  if (!entry) return null;

  const isExpense = entry.type === 'expense';
  const docs = Array.isArray(entry.documents) ? entry.documents : [];

  const handleDelete = async () => {
    if (!onDelete || !window.confirm('Bu düzeltme kaydını silmek istediğine emin misin?')) return;
    setDeleting(true);
    try {
      await onDelete(entry);
      onClose();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={deleting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{translate('report.adjustment_detail', { _: 'Düzeltme Detayı' })}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.25} sx={{ mt: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {translate('report.type', { _: 'Tip' })}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatSettlementEntryType(entry, translate)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {translate('report.item_note', { _: 'Kalem' })}
            </Typography>
            <Typography variant="body2" fontWeight={600} textAlign="right">
              {formatSettlementEntryLabel(entry, translate)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {translate('report.amount', { _: 'Tutar' })}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={700}
              color={isExpense ? 'error.main' : 'success.main'}
            >
              {isExpense ? '− ' : '+ '}
              {money(entry.amount)}
            </Typography>
          </Box>
          {entry.note ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>
                {translate('report.note', { _: 'Not' })}
              </Typography>
              <Typography variant="body2">{entry.note}</Typography>
            </Box>
          ) : null}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {translate('report.date', { _: 'Tarih' })}
            </Typography>
            <Typography variant="body2">{formatTs(entry.createdAt)}</Typography>
          </Box>
          {entry.createdBy ? (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {translate('report.added_by', { _: 'Ekleyen' })}
              </Typography>
              <Typography variant="body2">{entry.createdBy}</Typography>
            </Box>
          ) : null}

          <Divider sx={{ my: 0.5 }} />

          <Typography variant="subtitle2" fontWeight={700}>
            {translate('report.documents', { _: 'Evraklar' })}
          </Typography>
          {docs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {translate('report.no_documents', { _: 'Evrak yok' })}
            </Typography>
          ) : (
            <Stack spacing={0.5}>
              {docs.map((doc, i) => {
                const href = resolveMediaUrl(doc.src);
                const label = doc.title || doc.name || `Belge ${i + 1}`;
                return (
                  <Link
                    key={`${doc.src}-${i}`}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    variant="body2"
                  >
                    {label}
                  </Link>
                );
              })}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {isAdmin && onDelete ? (
          <Button color="error" onClick={handleDelete} disabled={deleting} sx={{ mr: 'auto' }}>
            {deleting ? <CircularProgress size={18} /> : translate('ra.action.delete', { _: 'Sil' })}
          </Button>
        ) : null}
        <Button onClick={onClose} disabled={deleting} variant="contained">
          {translate('ra.action.close', { _: 'Kapat' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettlementEntryDetailModal;
